import { useRef, useState } from 'react';
import customerOrderService from '../services/customerOrderService';

function tx(enStr, esStr, lang) {
  return lang === 'es' ? esStr : enStr;
}

// ── Signature canvas — same hand-rolled pattern as PodCaptureModal.js ─────────
function SignatureCanvas({ onSigned }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPoint = useRef(null);

  const getPoint = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * (canvas.width / rect.width),
      y: (src.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDraw = (e) => { drawing.current = true; lastPoint.current = getPoint(e, canvasRef.current); e.preventDefault(); };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pt = getPoint(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.strokeStyle = '#000'; ctx.lineWidth = 2; ctx.lineCap = 'round';
    ctx.stroke();
    lastPoint.current = pt;
  };
  const endDraw = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onSigned(canvasRef.current.toDataURL('image/png'));
  };
  const clear = () => {
    canvasRef.current.getContext('2d').clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    onSigned(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={560} height={160}
        style={{ width: '100%', height: 'auto', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', touchAction: 'none', cursor: 'crosshair' }}
        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
      />
      <button type="button" onClick={clear} style={CLEAR_BTN_STYLE}>Clear</button>
    </div>
  );
}

const FIELD_STYLE = {
  width: '100%', padding: '8px 10px', fontSize: '0.85rem',
  border: '1px solid #d1d5db', borderRadius: 6, marginTop: 4, boxSizing: 'border-box',
};
const LABEL_STYLE = { fontSize: '0.78rem', fontWeight: 600, color: '#374151' };
const SECTION_STYLE = {
  background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb',
  padding: '18px 20px', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};
const CLEAR_BTN_STYLE = {
  marginTop: 6, padding: '4px 12px', fontSize: '0.78rem', fontWeight: 600,
  border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer',
};

function Field({ label, value, onChange, lang, esLabel, type = 'text', required }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <span style={LABEL_STYLE}>{tx(label, esLabel || label, lang)}{required && ' *'}</span>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} style={FIELD_STYLE} required={required} />
    </label>
  );
}

// Two fields side by side on the same line — used for Name/Title pairs so they read as one
// logical row instead of two stacked full-width inputs.
function FieldRow({ children }) {
  return (
    <div style={{ display: 'flex', gap: 12 }}>
      {children.map((child, i) => <div key={i} style={{ flex: 1 }}>{child}</div>)}
    </div>
  );
}

const REQUIRED_APP_FIELDS = [
  'fullName', 'title', 'companyName', 'businessType', 'inBusinessSince', 'phone', 'email', 'fax',
  'registeredAddress', 'cityStateZip', 'howLongAtAddress',
  'apResponsibleName', 'apResponsibleTitle', 'apResponsibleEmail',
  'bankName', 'bankAddress', 'bankPhone', 'accountNumber', 'accountType',
];

const EMPTY_REFERENCE = { companyName: '', address: '', cityStateZip: '', phone: '', email: '', referenceName: '' };

function isReferenceComplete(r) {
  return !!(r.companyName.trim() && r.referenceName.trim() && (r.phone.trim() || r.email.trim()));
}

export default function CreditApplicationForm({ token, lang, customerName, onSubmitted }) {
  const [form, setForm] = useState({
    fullName: '', title: '', inBusinessSince: '', companyName: customerName || '', businessType: '',
    phone: '', email: '', fax: '', registeredAddress: '', cityStateZip: '', howLongAtAddress: '',
    apResponsibleName: '', apResponsibleTitle: '', apResponsibleEmail: '',
    bankName: '', bankAddress: '', bankPhone: '', accountNumber: '', accountType: '',
    signedByName: '',
  });
  const [references, setReferences] = useState([{ ...EMPTY_REFERENCE }, { ...EMPTY_REFERENCE }, { ...EMPTY_REFERENCE }]);
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (value) => setForm(f => ({ ...f, [key]: value }));
  const setRef = (idx, key, value) => setReferences(refs => refs.map((r, i) => i === idx ? { ...r, [key]: value } : r));

  const handleSubmit = async (e) => {
    e.preventDefault();

    const missing = REQUIRED_APP_FIELDS.filter(key => !String(form[key] || '').trim());
    if (missing.length) {
      setError(tx('Please complete all required fields before submitting.', 'Complete todos los campos requeridos antes de enviar.', lang));
      return;
    }
    const completeReferences = references.filter(isReferenceComplete);
    if (completeReferences.length < 2) {
      setError(tx(
        'Please provide at least 2 complete trade references (company, contact name, and phone or email).',
        'Proporcione al menos 2 referencias comerciales completas (empresa, nombre de contacto y teléfono o correo).',
        lang
      ));
      return;
    }
    if (!form.signedByName.trim() || !signatureDataUrl) {
      setError(tx('Please sign the application before submitting.', 'Por favor firme la solicitud antes de enviarla.', lang));
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await customerOrderService.submitCreditApp(token, {
        ...form,
        references: completeReferences,
        signatureDataUrl,
      });
      onSubmitted();
    } catch (err) {
      setError(err?.response?.data?.error || tx('Failed to submit. Please try again.', 'Error al enviar. Intenta de nuevo.', lang));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '24px 16px 64px' }}>
      <h5 style={{ fontWeight: 700, color: '#111827', marginBottom: 6 }}>
        {tx('Credit Application', 'Solicitud de Crédito', lang)}
      </h5>
      <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 20 }}>
        {tx(
          'Please complete this application before placing your first order. This only needs to be done once.',
          'Complete esta solicitud antes de realizar su primer pedido. Esto solo debe hacerse una vez.',
          lang
        )}
      </p>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, padding: '10px 14px', fontSize: '0.82rem', marginBottom: 16 }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={SECTION_STYLE}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 12, color: '#111827' }}>
            {tx('Business Contact Information', 'Información de Contacto del Negocio', lang)}
          </div>
          <FieldRow>
            <Field label="Full Name" esLabel="Nombre Completo" value={form.fullName} onChange={set('fullName')} lang={lang} required />
            <Field label="Title" esLabel="Cargo" value={form.title} onChange={set('title')} lang={lang} required />
          </FieldRow>
          <Field label="Company Name" esLabel="Nombre de la Empresa" value={form.companyName} onChange={set('companyName')} lang={lang} required />
          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={LABEL_STYLE}>{tx('Business Type', 'Tipo de Negocio', lang)} *</span>
            <select value={form.businessType} onChange={e => set('businessType')(e.target.value)} style={FIELD_STYLE} required>
              <option value="">—</option>
              <option value="sole_proprietorship">{tx('Sole Proprietorship', 'Propietario Único', lang)}</option>
              <option value="partnership">{tx('Partnership', 'Sociedad', lang)}</option>
              <option value="corporation">{tx('Corporation', 'Corporación', lang)}</option>
              <option value="llc">LLC</option>
            </select>
          </label>
          <Field label="In Business Since" esLabel="En Operación Desde" value={form.inBusinessSince} onChange={set('inBusinessSince')} lang={lang} required />
          <Field label="Phone" esLabel="Teléfono" value={form.phone} onChange={set('phone')} lang={lang} required />
          <Field label="E-mail" esLabel="Correo Electrónico" value={form.email} onChange={set('email')} type="email" lang={lang} required />
          <Field label="Fax" esLabel="Fax" value={form.fax} onChange={set('fax')} lang={lang} required />
          <Field label="Registered Company Address" esLabel="Dirección Registrada de la Empresa" value={form.registeredAddress} onChange={set('registeredAddress')} lang={lang} required />
          <Field label="City, State ZIP Code" esLabel="Ciudad, Estado, Código Postal" value={form.cityStateZip} onChange={set('cityStateZip')} lang={lang} required />
          <Field label="How Long at Current Address?" esLabel="¿Cuánto Tiempo en la Dirección Actual?" value={form.howLongAtAddress} onChange={set('howLongAtAddress')} lang={lang} required />
          <FieldRow>
            <Field label="A/P Responsible Name" esLabel="Nombre del Responsable de Cuentas por Pagar" value={form.apResponsibleName} onChange={set('apResponsibleName')} lang={lang} required />
            <Field label="A/P Responsible Title" esLabel="Cargo del Responsable de Cuentas por Pagar" value={form.apResponsibleTitle} onChange={set('apResponsibleTitle')} lang={lang} required />
          </FieldRow>
          <Field label="A/P Responsible E-mail" esLabel="Correo del Responsable de Cuentas por Pagar" value={form.apResponsibleEmail} onChange={set('apResponsibleEmail')} type="email" lang={lang} required />
        </div>

        <div style={SECTION_STYLE}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 12, color: '#111827' }}>
            {tx('Business and Credit Information', 'Información Comercial y Crediticia', lang)}
          </div>
          <Field label="Bank Name" esLabel="Nombre del Banco" value={form.bankName} onChange={set('bankName')} lang={lang} required />
          <Field label="Primary Bank Address / City, State ZIP" esLabel="Dirección Principal del Banco / Ciudad, Estado, C.P." value={form.bankAddress} onChange={set('bankAddress')} lang={lang} required />
          <Field label="Bank Phone" esLabel="Teléfono del Banco" value={form.bankPhone} onChange={set('bankPhone')} lang={lang} required />
          <Field label="Account Number" esLabel="Número de Cuenta" value={form.accountNumber} onChange={set('accountNumber')} lang={lang} required />
          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={LABEL_STYLE}>{tx('Type of Account', 'Tipo de Cuenta', lang)} *</span>
            <select value={form.accountType} onChange={e => set('accountType')(e.target.value)} style={FIELD_STYLE} required>
              <option value="">—</option>
              <option value="savings">{tx('Savings', 'Ahorros', lang)}</option>
              <option value="checking">{tx('Checking', 'Corriente', lang)}</option>
            </select>
          </label>
          <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 4 }}>
            {tx('Your account number is encrypted and only viewable by an administrator after a security verification.', 'Su número de cuenta está encriptado y solo puede verlo un administrador tras una verificación de seguridad.', lang)}
          </div>
        </div>

        <div style={SECTION_STYLE}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 2, color: '#111827' }}>
            {tx('Business / Trade References', 'Referencias Comerciales', lang)}
          </div>
          <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginBottom: 12 }}>
            {tx(
              'At least 2 references are required — company, contact name, and phone or e-mail.',
              'Se requieren al menos 2 referencias — empresa, nombre de contacto y teléfono o correo.',
              lang
            )}
          </div>
          {references.map((ref, idx) => (
            <div key={idx} style={{ borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none', paddingTop: idx > 0 ? 12 : 0, marginTop: idx > 0 ? 12 : 0 }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#6b7280', marginBottom: 6 }}>
                {tx('Reference', 'Referencia', lang)} {idx + 1}
              </div>
              <Field label="Company Name" esLabel="Nombre de la Empresa" value={ref.companyName} onChange={v => setRef(idx, 'companyName', v)} lang={lang} />
              <Field label="Address" esLabel="Dirección" value={ref.address} onChange={v => setRef(idx, 'address', v)} lang={lang} />
              <Field label="City, State ZIP Code" esLabel="Ciudad, Estado, Código Postal" value={ref.cityStateZip} onChange={v => setRef(idx, 'cityStateZip', v)} lang={lang} />
              <Field label="Phone" esLabel="Teléfono" value={ref.phone} onChange={v => setRef(idx, 'phone', v)} lang={lang} />
              <Field label="E-mail" esLabel="Correo Electrónico" value={ref.email} onChange={v => setRef(idx, 'email', v)} type="email" lang={lang} />
              <Field label="Reference Name" esLabel="Nombre de Contacto" value={ref.referenceName} onChange={v => setRef(idx, 'referenceName', v)} lang={lang} />
            </div>
          ))}
        </div>

        <div style={SECTION_STYLE}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 10, color: '#111827' }}>
            {tx('Agreement', 'Acuerdo', lang)}
          </div>
          <ol style={{ fontSize: '0.78rem', color: '#4b5563', paddingLeft: 18, marginBottom: 0 }}>
            <li>{tx('All invoices are to be paid 15 days from the date of the invoice.', 'Todas las facturas deben pagarse 15 días después de la fecha de la factura.', lang)}</li>
            <li>{tx('Claims arising from invoices must be made within seven working days.', 'Los reclamos sobre facturas deben hacerse dentro de siete días hábiles.', lang)}</li>
            <li>{tx('Payments made by credit or debit card are subject to a 3% merchant processing surcharge, which will be added to the invoice total at the time of payment.', 'Los pagos con tarjeta de crédito o débito están sujetos a un recargo del 3% por procesamiento, que se añadirá al total de la factura al momento del pago.', lang)}</li>
            <li>{tx('By submitting this application, you authorize us to make inquiries into the banking and business/trade references that you have supplied.', 'Al enviar esta solicitud, usted autoriza a hacer consultas sobre las referencias bancarias y comerciales que ha proporcionado.', lang)}</li>
          </ol>
        </div>

        <div style={SECTION_STYLE}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 12, color: '#111827' }}>
            {tx('Signature', 'Firma', lang)}
          </div>
          <Field label="Signed Name" esLabel="Nombre de Quien Firma" value={form.signedByName} onChange={set('signedByName')} lang={lang} required />
          <div style={{ marginTop: 8 }}>
            <span style={LABEL_STYLE}>{tx('Sign below', 'Firme abajo', lang)} *</span>
            <div style={{ marginTop: 4 }}>
              <SignatureCanvas onSigned={setSignatureDataUrl} />
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          style={{
            width: '100%', padding: '12px', fontSize: '0.95rem', fontWeight: 700,
            border: 'none', borderRadius: 8, color: '#fff',
            background: submitting ? '#93c5fd' : '#3b82f6', cursor: submitting ? 'default' : 'pointer',
          }}
        >
          {submitting
            ? tx('Submitting…', 'Enviando…', lang)
            : tx('Submit Application', 'Enviar Solicitud', lang)}
        </button>
      </form>
    </div>
  );
}
